import { computed, ref, watch, type Ref } from 'vue';

import type { AdConsent } from '~/types/AdConsent';

export type AdConsentResult = {
   consent: AdConsent | null;
   error: { status?: number } | null;
};

export type AdConsentApi = {
   fetchConsent: () => Promise<AdConsentResult>;
   saveConsent: (granted: boolean) => Promise<AdConsentResult>;
};

// The last answer the server gave, whoever asked. Every instance follows it, so
// the settings switch shows a choice made in the popup if both are mounted.
export const latestAdConsent = ref<AdConsent | null>(null);

export const COOKIE_POLICY_URL = 'https://talktime.ae/cookie-policy';
export const COOKIE_POLICY_URL_AR = 'https://talktime.ae/ar/cookie-policy';

export const cookiePolicyUrl = (locale: string) =>
   locale === 'ar' ? COOKIE_POLICY_URL_AR : COOKIE_POLICY_URL;

// The switch flips the moment it is clicked and goes back if the server says no,
// so the shown state only ever disagrees with the stored one while a save is in
// flight, and `saving` locks the control for exactly that window. The server's
// reply, not the click, is what the switch settles on after a successful save.
export const useAdConsent = (api: AdConsentApi, readOnly: Ref<boolean> = ref(false)) => {
   const granted = ref(false);
   // The last answer the server gave this instance, whole.
   const lastConsent = ref<AdConsent | null>(null);
   const loaded = ref(false);
   const loading = ref(false);
   const loadFailed = ref(false);
   const saving = ref(false);
   const saveFailed = ref(false);
   // Only an explicit `true` from the server counts; absent means "do not ask".
   const shouldPrompt = ref(false);
   // The API refuses the choice to an impersonating admin, so the control is
   // view-only for them rather than flipping and reverting.
   const locked = computed(() => saving.value || readOnly.value);

   const adopt = (answer: AdConsent) => {
      granted.value = answer.granted;
      shouldPrompt.value = answer.should_prompt === true;
      lastConsent.value = answer;
      latestAdConsent.value = answer;
   };

   watch(
      latestAdConsent,
      (consent) => {
         if (consent && loaded.value && !saving.value) granted.value = consent.granted;
      },
      { flush: 'sync' },
   );

   const load = async () => {
      loading.value = true;
      loadFailed.value = false;
      const { consent, error } = await api.fetchConsent();
      loading.value = false;
      // An API without this route answers 404: stay hidden rather than show every
      // client an error for a setting that does not exist yet.
      if (error?.status === 404) return;
      if (error || !consent) {
         loadFailed.value = true;
         return;
      }
      adopt(consent);
      loaded.value = true;
   };

   // Resolves to whether the server stored the choice.
   const setGranted = async (next: boolean): Promise<boolean> => {
      if (!loaded.value || locked.value) return false;
      const previous = granted.value;
      saveFailed.value = false;
      granted.value = next;
      saving.value = true;
      const { consent, error } = await api.saveConsent(next);
      saving.value = false;
      if (error || !consent) {
         granted.value = previous;
         saveFailed.value = true;
         return false;
      }
      adopt(consent);
      return true;
   };

   return {
      granted,
      consent: lastConsent,
      loaded,
      loading,
      loadFailed,
      saving,
      locked,
      saveFailed,
      shouldPrompt,
      load,
      setGranted,
   };
};
